import React, { useState } from 'react';

import classNames from 'classnames';
import Modal from 'components/cosmos/modal';
import Icon from 'components/icons';
import I18n from 'components/translate/I18n';
import isEmpty from 'lodash/isEmpty';
import {
  EVENT_ACTIONS,
  EVENTS_INTERACTIONS,
  EVENTS_CATEGORY
} from 'modules/app/services/datahub/constants';
import DatahubService from 'modules/app/services/datahub/DatahubService';
import { buildInteractionEventData } from 'modules/app/services/datahub/helper';
import { CATEGORIES, ACTIONS } from 'modules/app/services/ga/constants';
import GtagService from 'modules/app/services/ga/GtagService';
import { MODULE_NAME_MAX_CHARACTERS } from 'modules/content/constants';
import PropTypes from 'prop-types';

import ClassSelectionStep from './ClassSelectionStep';
import { DRIPPING_PARAMS, DRIPPING_OPTION } from './constants';
import DrippingSelectionStep from './DrippingSelectionStep';
import ModuleSelectionStep from './ModuleSelectionStep';

import { DRIPPING_TIME } from '../dripping/constants';

import './DuplicationModal.scss';

const EVENT_STEP = {
  0: 'First',
  1: 'Second',
  2: 'Third'
};

const renderModuleSelectionStep = (callbacks, { id, name }) => (
  <ModuleSelectionStep
    moduleId={id}
    moduleName={name}
    onChange={callbacks.onChange}
    onModuleNameChange={callbacks.onModuleNameChange}
  />
);

const renderClassSelectionStep = (onChange, { classes }) => (
  <ClassSelectionStep onChange={onChange} moduleClasses={classes} />
);

const renderDrippingSelectionStep = (onChange) => (
  <DrippingSelectionStep onDrippingChange={onChange} />
);

const steps = [
  renderModuleSelectionStep,
  renderClassSelectionStep,
  renderDrippingSelectionStep
];

const renderStep = (currentStep, callbacks, id, name, classes) =>
  steps[currentStep](callbacks[currentStep], { id, name, classes });

const renderMarker = (key, currentStep) => {
  if (key < currentStep) {
    return (
      <span className="current-step duplication-modal__tracker__current-step">
        <Icon prefix="fas" iconName="check-circle" />
      </span>
    );
  }

  const atCurrentStep = key === currentStep;
  const markerClassname = classNames({
    'duplication-modal__tracker__current-step': atCurrentStep,
    'duplication-modal__tracker__marker': !atCurrentStep
  });

  return (
    <span className={markerClassname}>
      {atCurrentStep && <span className="duplication-modal__tracker__marker" />}
    </span>
  );
};

const renderTrackerMarker = (key, currentStep) => (
  <>
    {renderMarker(key, currentStep)}
    {/* eslint-disable-next-line @typescript-eslint/no-magic-numbers */}
    {key < 2 && <hr className="duplication-modal__tracker__trace" />}
  </>
);

const getEventLabel = (dripping) => {
  let label = '';

  if (!dripping.liberationType) {
    label += 'liberationType: none';
  } else {
    label += `liberationType: ${dripping.liberationType} | `;

    if (dripping.liberationDays) {
      label += `liberationDays: ${dripping.liberationDays}`;
    } else if (dripping.liberationDate) {
      label += `liberationDate: ${new Date(
        dripping.liberationDate
      ).toUTCString()}`;
    }
  }

  return label;
};

const isModuleNameValid = (value) => {
  return !isEmpty(value) && value.length <= MODULE_NAME_MAX_CHARACTERS;
};

const DuplicationModal = ({ isOpen, onCancel, module, onConfirm }) => {
  const [moduleName, setModuleName] = useState(module.name);
  const [currentStep, setCurrentStep] = useState(0);
  const [checkedPages, setCheckedPages] = useState([]);
  const [checkedClasses, setCheckedClasses] = useState([]);
  const [dripping, setDripping] = useState({});
  const [disableProgession, setDisableProgression] = useState(
    !isModuleNameValid(module.name)
  );

  const onSetModuleName = (value) => {
    setDisableProgression(!isModuleNameValid(value) || isEmpty(checkedPages));
    setModuleName(value);
  };

  const onCheckedPagesChange = (value) => {
    setDisableProgression(isEmpty(value) || !isModuleNameValid(moduleName));

    setCheckedPages(value);
  };

  const onCheckedClassChange = (value) => {
    setDisableProgression(isEmpty(value));

    setCheckedClasses(value);
  };

  const onDrippingChange = (value) => {
    setDripping({ ...dripping, ...value });
  };

  const isDuplicationDisabled = () => {
    if (isEmpty(dripping)) {
      return true;
    }

    const type = dripping[DRIPPING_PARAMS.type];
    const date = dripping[DRIPPING_PARAMS[DRIPPING_OPTION.BY_DATE]];
    const day = dripping[DRIPPING_PARAMS[DRIPPING_OPTION.BY_DAYS]];

    if (!type) {
      return false;
    }

    if (type === DRIPPING_OPTION.BY_DATE) {
      return !date;
    }

    if (type === DRIPPING_OPTION.BY_DAYS) {
      const dayIsValid = day && day <= DRIPPING_TIME.DAYS;

      return !dayIsValid;
    }
  };

  const stepCallbacks = [
    { onChange: onCheckedPagesChange, onModuleNameChange: onSetModuleName },
    onCheckedClassChange,
    onDrippingChange
  ];

  const goToNextStep = () => setCurrentStep(currentStep + 1);
  const goToPreviousStep = () => setCurrentStep(currentStep - 1);

  const atFirstStep = currentStep === 0;
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  const atLastStep = currentStep === 2;

  const duplicate = () => {
    const params = {
      classes: checkedClasses,
      pages: checkedPages,
      ...dripping,
      module: module.id,
      newName: moduleName
    };

    const label = getEventLabel(dripping);

    GtagService.sendClick({
      category: CATEGORIES.CLUB_ADMIN_CONTENT,
      where: ACTIONS.DUPLICATION_CONTENT.DUPLICATE_MODULE,
      label
    });

    DatahubService.sendEvent({
      action: EVENT_ACTIONS.INTERACT,
      event: buildInteractionEventData({
        category: EVENTS_CATEGORY.DUPLICATION_CONTENT.DUPLICATE_MODULE,
        label,
        action: EVENTS_INTERACTIONS.CLICK
      })
    });

    onConfirm(params);
  };

  const closeDuplicationModal = () => {
    const label = `${EVENT_STEP[currentStep]} step`;

    GtagService.sendClick({
      category: CATEGORIES.CLUB_ADMIN_CONTENT,
      where: ACTIONS.DUPLICATION_CONTENT.CLOSE_MODAL,
      label
    });

    DatahubService.sendEvent({
      action: EVENT_ACTIONS.INTERACT,
      event: buildInteractionEventData({
        category: EVENTS_CATEGORY.DUPLICATION_CONTENT.CLOSE_MODAL,
        label,
        action: EVENTS_INTERACTIONS.CLICK
      })
    });

    onCancel();
  };

  const renderProgression = () =>
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    Array.from(Array(3)).map((item, key) =>
      renderTrackerMarker(key, currentStep)
    );

  const header = (
    <h3 className="duplication-modal__header">
      <I18n label="content_list.content_duplication.title" />
    </h3>
  );

  const footer = (
    <div className="duplication-modal__footer">
      {!atFirstStep && (
        <button
          className="hot-button hot-button--tertiary _mr-4"
          onClick={goToPreviousStep}
        >
          <I18n label="general.back" />
        </button>
      )}
      {!atLastStep && (
        <button
          className="hot-button hot-button--primary"
          onClick={goToNextStep}
          disabled={disableProgession}
        >
          <I18n label="general.next" />
          <Icon prefix="fal" iconName="arrow-right" className="_ml-2" />
        </button>
      )}
      {atLastStep && (
        <button
          className="hot-button hot-button--primary"
          onClick={duplicate}
          disabled={isDuplicationDisabled()}
        >
          <I18n label="content_list.content_duplication.title" />
        </button>
      )}
    </div>
  );

  return (
    <Modal
      header={header}
      footer={footer}
      open={isOpen}
      onClose={closeDuplicationModal}
      position="centered"
      className="duplication-modal"
    >
      <div className="duplication-modal__body">
        <div className="duplication-modal__tracker">{renderProgression()}</div>
        {renderStep(
          currentStep,
          stepCallbacks,
          module.id,
          moduleName,
          module.classes
        )}
      </div>
    </Modal>
  );
};

DuplicationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  module: PropTypes.object.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default DuplicationModal;
